import type { ReviewAiJobProgressEventEntity } from '@/entities/review-ai-job-progress-event.entity';
import {
    OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES,
    formatKnownMarketplaceApiErrorMessageFromDescriptor,
    type MarketplaceApiErrorLocale,
} from '@/shared/errors/marketplace-api-error-message';

export type ReviewAiJobMessageLocale = 'ru' | 'en' | 'uz' | 'uzCyrl';

export type ReviewAiJobMessages = {
    retrying: string;
    retryingAttempt: string;
    failed: string;
    canceled: string;
    emptyAnswer: string;
    disconnected: string;
    offerNotAccepted: string;
};

export const REVIEW_AI_JOB_MESSAGES = {
    ru: {
        retrying: 'Сервис ИИ временно не принял запрос. Повторяем автоматически.',
        retryingAttempt: 'Попытка {retryAttempt} из {maxAttempts}.',
        failed: 'Ошибка сервиса ИИ. Не удалось сгенерировать ответ',
        canceled: 'Формирование ИИ-ответа отменено',
        emptyAnswer: 'AI не вернул сгенерированный ответ',
        disconnected: 'Не удалось получить результат AI-задачи',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES.ru,
    },
    en: {
        retrying: 'The AI service could not accept the request yet. Retrying automatically.',
        retryingAttempt: 'Attempt {retryAttempt} of {maxAttempts}.',
        failed: 'AI service error. Could not generate an answer',
        canceled: 'AI answer generation was canceled',
        emptyAnswer: 'AI did not return a generated answer',
        disconnected: 'Could not receive the AI job result',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES.en,
    },
    uz: {
        retrying: 'AI xizmati so‘rovni hozircha qabul qilmadi. Avtomatik qayta urinilmoqda.',
        retryingAttempt: '{maxAttempts} urinishdan {retryAttempt}-urinish.',
        failed: 'AI xizmati xatosi. Javob yaratilmadi',
        canceled: 'AI javob yaratish bekor qilindi',
        emptyAnswer: 'AI yaratilgan javobni qaytarmadi',
        disconnected: 'AI vazifasi natijasini olib bo‘lmadi',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES.uz,
    },
    uzCyrl: {
        retrying: 'AI хизмати сўровни ҳозирча қабул қилмади. Автоматик қайта уринилмоқда.',
        retryingAttempt: '{maxAttempts} уринишдан {retryAttempt}-уриниш.',
        failed: 'AI хизмати хатоси. Жавоб яратилмади',
        canceled: 'AI жавоб яратиш бекор қилинди',
        emptyAnswer: 'AI яратилган жавобни қайтармади',
        disconnected: 'AI вазифаси натижасини олиб бўлмади',
        offerNotAccepted: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES['uz-Cyrl'],
    },
} as const satisfies Record<ReviewAiJobMessageLocale, ReviewAiJobMessages>;

export const getReviewAiJobMessages = (locale: ReviewAiJobMessageLocale = 'ru'): ReviewAiJobMessages =>
    REVIEW_AI_JOB_MESSAGES[locale] ?? REVIEW_AI_JOB_MESSAGES.ru;

type ReviewAiJobTerminalMessageEvent = Pick<
    ReviewAiJobProgressEventEntity,
    'stage' | 'errorMessage' | 'errorCode' | 'errorReason'
>;

type ReviewAiJobRetryingMessageEvent = Pick<ReviewAiJobProgressEventEntity, 'stage' | 'retryAttempt' | 'maxAttempts'>;

const formatReviewAiJobMessage = (
    template: string,
    replacements: Readonly<Record<string, string | number>>,
): string => {
    let result = template;
    for (const [name, value] of Object.entries(replacements)) {
        result = result.replace(`{${name}}`, String(value));
    }

    return result;
};

const toMarketplaceApiErrorLocale = (locale: ReviewAiJobMessageLocale): MarketplaceApiErrorLocale =>
    locale === 'uzCyrl' ? 'uz-Cyrl' : locale;

export const resolveReviewAiJobRetryingMessage = ({
    event,
    locale = 'ru',
}: {
    event: ReviewAiJobRetryingMessageEvent;
    locale?: ReviewAiJobMessageLocale;
}): string => {
    const messages = getReviewAiJobMessages(locale);
    if (!event.retryAttempt || !event.maxAttempts) {
        return messages.retrying;
    }

    return `${messages.retrying} ${formatReviewAiJobMessage(messages.retryingAttempt, {
        retryAttempt: event.retryAttempt,
        maxAttempts: event.maxAttempts,
    })}`;
};

export const resolveReviewAiJobTerminalMessage = ({
    event,
    locale = 'ru',
}: {
    event: ReviewAiJobTerminalMessageEvent;
    locale?: ReviewAiJobMessageLocale;
}): string => {
    const messages = getReviewAiJobMessages(locale);
    const knownMessage = formatKnownMarketplaceApiErrorMessageFromDescriptor(
        event,
        toMarketplaceApiErrorLocale(locale),
    );
    if (knownMessage) {
        return knownMessage;
    }

    const eventErrorMessage = event.errorMessage?.trim();
    if (eventErrorMessage) {
        return eventErrorMessage;
    }

    return event.stage === 'canceled' ? messages.canceled : messages.failed;
};
